type NormalizeSvgEmbedOptions = {
    x?: number;
    y?: number;
    mode?: 'cover' | 'contain' | string;
};

const svgTagRegex = /<svg\b([^>]*)>/i;

const attrRegex = (name: string): RegExp =>
    new RegExp(`\\b${name}=(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');

const getAttrValue = (attrs: string, name: string): string => {
    const match = attrs.match(attrRegex(name));
    return match ? match[1] || match[2] || match[3] || '' : '';
};

const parseNumericDimension = (value: unknown): number | null => {
    if (!value) return null;
    const raw = String(value).trim();
    if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);
    if (/^\d+(\.\d+)?px$/i.test(raw)) return Number(raw.replace(/px$/i, ''));
    return null;
};

const getAxisAlign = (value: number, axis: 'x' | 'y'): string => {
    if (value <= 33) return axis === 'x' ? 'xMin' : 'YMin';
    if (value >= 67) return axis === 'x' ? 'xMax' : 'YMax';
    return axis === 'x' ? 'xMid' : 'YMid';
};

const replaceOrAddAttr = (
    attrs: string,
    name: string,
    value: string
): string => {
    const pattern = new RegExp(`\\s${name}=("[^"]*"|'[^']*'|[^\\s>]+)`, 'i');
    if (pattern.test(attrs)) {
        return attrs.replace(pattern, ` ${name}="${value}"`);
    }
    return `${attrs} ${name}="${value}"`;
};

export function normalizeSvgEmbedMarkup(
    html: unknown,
    options: NormalizeSvgEmbedOptions = { x: 50, y: 50 }
): string {
    if (!html || typeof html !== 'string') return '';
    if (!svgTagRegex.test(html)) return html;

    const {
        x = 50,
        y = 50,
        mode = 'cover',
    } = options && typeof options === 'object' ? options : {};

    const alignX = getAxisAlign(x ?? 50, 'x');
    const alignY = getAxisAlign(y ?? 50, 'y');
    const fitMode = mode === 'contain' ? 'meet' : 'slice';
    const preserveAspectRatio = `${alignX}${alignY} ${fitMode}`;

    return html.replace(svgTagRegex, (_match, attrs: string = '') => {
        let next = attrs || '';
        const widthAttr = getAttrValue(next, 'width');
        const heightAttr = getAttrValue(next, 'height');
        const viewBoxAttr = getAttrValue(next, 'viewBox');
        const widthValue = parseNumericDimension(widthAttr);
        const heightValue = parseNumericDimension(heightAttr);

        next = replaceOrAddAttr(
            next,
            'preserveAspectRatio',
            preserveAspectRatio
        );

        if (!viewBoxAttr && widthValue && heightValue) {
            next = replaceOrAddAttr(
                next,
                'viewBox',
                `0 0 ${widthValue} ${heightValue}`
            );
        }

        next = replaceOrAddAttr(next, 'width', '100%');
        next = replaceOrAddAttr(next, 'height', '100%');
        return `<svg${next}>`;
    });
}
